/**
 * ── THE LISTING TABLE'S PER-HOME ROWS (audit rows 10 + 22) ────────────────────
 * Port of the monolith's `__rowOf` + the row bucketing / groups emit
 * (index.html L13286, L13463–13487), the per-row clear (L13584–13597) and the
 * `<!-- HOME n -->` render pass (L8720–8735).
 *
 * WHY THIS EXISTS. ECH_ListingTable declares 73 fields — 3 top-level plus 10
 * homes × 7 — which rendered as one flat wall of 73 inputs. Folding every
 * `home<N><Field>` key into its own collapsible group makes the panel read as ten
 * homes. The BLOCK is untouched: unused rows are dropped at render, which is why
 * this needs no new block variants from Layne (she would otherwise have to
 * maintain ten).
 *
 * NOT THE LEGACY `listrows` EDITOR. That one edits a pipe-delimited string on the
 * hand-built `listing` block — a different data shape entirely, and it stays.
 */

/** the 7 suffixes one home row owns (monolith L13590) */
export const ROW_FIELDS = ["Homesite", "Plan", "SqFt", "Beds", "Baths", "Price", "Url"] as const;

export interface RowRef { n: number; f: string }

/** monolith `__rowOf` (L13286) */
export function rowOf(k: unknown): RowRef | null {
  const mm = /^home(\d+)([A-Za-z]+)$/.exec(String(k || ""));
  return mm ? { n: +mm[1], f: mm[2] } : null;
}

/** does this block declare home rows at all? (monolith `__hasRows`, L13287) */
export function hasHomeRows(fields?: readonly { k?: string }[] | null): boolean {
  try { return (fields || []).some((f) => !!rowOf(f.k)); } catch { return false; }
}

export interface RowSummary { used: boolean; text: string }

/**
 * monolith L13470–13475. `used` is Homesite OR Plan — the same test the render
 * pass drops on, so the panel can never disagree with the email about whether a
 * row ships. The two spaces around the middot are the monolith's.
 */
export function rowSummary(d: Record<string, unknown>, n: number): RowSummary {
  const hs = String(d["home" + n + "Homesite"] || "").trim();
  const pl = String(d["home" + n + "Plan"] || "").trim();
  const pr = String(d["home" + n + "Price"] || "").trim();
  const used = !!(hs || pl);
  return {
    used,
    text: used
      ? [hs, pl].filter(Boolean).join(" / ") + (pr ? "  ·  " + pr : "")
      : "empty — not shown in the email",
  };
}

/**
 * The × on a row group: empty its 7 fields, and the render pass then drops the
 * row (monolith L13588–13595).
 *
 * ONLY KEYS THE BLOCK ACTUALLY DECLARES. The monolith guards with
 * `if(m.data[...]!==undefined)`, so clearing row 3 of a block that declares no
 * `home3Url` does not invent the key. Reproduced — an invented key would be
 * exported as an unmatched token.
 */
export function rowClearPatch(d: Record<string, unknown>, n: number | string): Record<string, string> {
  const patch: Record<string, string> = {};
  ROW_FIELDS.forEach((sfx) => {
    const k = "home" + n + sfx;
    if (d[k] !== undefined) patch[k] = "";
  });
  return patch;
}

/** a `<!-- HOME n -->` marker's own comment text, and nothing else's */
const HOME_MARKER = /^\s*HOME\s+(\d+)\s*$/;

/** every comment node under `root`, in document order */
function comments(root: Node, out: Comment[]): Comment[] {
  for (let c = root.firstChild; c; c = c.nextSibling) {
    if (c.nodeType === 8) out.push(c as Comment);
    else if (c.nodeType === 1) comments(c, out);
  }
  return out;
}

/**
 * The `<tr>` a HOME marker introduces — the marker's next sibling, whitespace
 * skipped. NOT "everything until the next marker": see the header below.
 */
function ownRow(mark: Comment): Element | null {
  let n: ChildNode | null = mark.nextSibling;
  /* the block indents its markup, so a whitespace text node sits between the marker
     and its row on any hand-formatted variant of this block */
  while (n && n.nodeType === 3 && !String(n.nodeValue || "").trim()) n = n.nextSibling;
  if (n && n.nodeType === 1 && (n as Element).tagName.toUpperCase() === "TR") return n as Element;
  return null;
}

/**
 * ── EMPTY LISTING ROWS ARE REMOVED, NOT LEFT BLANK (monolith L8720–8735) ──────
 * The block ships 10 fixed home rows and its own guardrail says unused rows
 * "must be deleted entirely, not left blank" — otherwise the email renders blank
 * striped rows. A row whose Homesite AND Plan are both empty is dropped here.
 *
 * ⚠️ THE MARKERS DO NOT DELIMIT THE ROWS (2026-08-20). This used to read the block
 * as `<!-- HOME n -->` … up to the next `<!-- HOME \d+ -->` or `$`, and that reading
 * corrupts a real send in TWO places on ECH_ListingTable's own markup (ContentID
 * 1028584, verified against the block cache pulled 2026-08-18):
 *
 *  1. THERE IS NOTHING AFTER `<!-- HOME 10 -->`. Its lookahead fell through to `$`,
 *     so emptying row 10 deleted the rest of the document — 5 closing `</table>`
 *     tags and the block's own `<!--END Com Table-->`. Measured: `</table>` went
 *     5 → 0 while `<table>` stayed 5. This is the COMMON path, not an edge case:
 *     `echDyn` writes "" for every home the community feed doesn't have, so any
 *     community with fewer than 10 available homesites shipped an unclosed table
 *     and nested every block below the Listing Table inside it.
 *  2. THE COLLECTION HEADER LIVES INSIDE ROW 5'S SEGMENT. The real marker order is
 *     `… HOME 4 | HOME 5 | 2ND COLLECTION | HOME 6 …`, so emptying row 5 also took
 *     `<!-- 2ND COLLECTION -->` and `{{collection2Name}}` with it — a table whose
 *     back half has lost its heading.
 *
 * The block's guardrail states the correct unit precisely: "Delete the full row
 * block FROM THE OPENING ROW TAG TO THE CLOSING ROW TAG." One `<tr>`. So the marker
 * is treated as a LABEL on the row that follows it, never as a range boundary, and a
 * marker whose next sibling is not a `<tr>` (a hand-edited block, two markers in a
 * row) removes nothing rather than guessing at a span.
 *
 * DOM PARSING, NOT REGEX — and this is the sibling module's prediction coming true.
 * `echAmen.amenTrimHtml` warns that "a regex that looks right will eat the wrong
 * `</td>` on some block eventually" over these nested tables; a bounded regex here
 * would still have to decide which of the row's `</tr>` closers is its own, and the
 * table nests 5 deep. Same failure posture as that function: `DOMParser` is missing
 * in a bare Node parity run, so the html comes back untouched there rather than
 * throwing.
 *
 * NO REMOVAL → NO ROUND-TRIP. Re-serialising is not free — the HTML parser inserts
 * `<tbody>` (5 of them on this block, +75 bytes), so a block nobody has emptied must
 * return its own bytes and not the parser's idea of them. That also keeps the
 * one-line-per-block export stable for the 10-of-10 case.
 *
 * Lifted out of `echRegistry`'s renderer in an earlier pass so it can be tested
 * directly against markup; the renderer calls this and nothing else changed.
 */
/**
 * THE ROWS THE MARKUP ACTUALLY DRAWS — the one list both renderings must agree on.
 * 2026-08-28.
 *
 * The block declares ten `home<N>*` field sets, but the fill RE-CUTS the markup to
 * however many rows the pick needs, and a `<!-- HOME n -->` the re-cut dropped can
 * never ship. So "which rows exist" is a fact about `__html`, not about the declared
 * fields — and reading it off the declared fields is what let the Inspector say
 * "10 of 10 shown" over a canvas drawing three (2026-08-28 audit, defects A and B).
 *
 * Regex rather than `DOMParser` on purpose: this has to answer in a bare Node parity
 * run and inside the fill, neither of which has a DOM, and a comment marker is not a
 * nesting problem — `dropEmptyHomeRows` needs the tree only because it removes rows.
 */
export function htmlRowNumbers(html: unknown): number[] {
  const out: number[] = [];
  const re = /<!--\s*HOME\s+(\d+)\s*-->/gi;
  const src = String(html || "");
  let m: RegExpExecArray | null = re.exec(src);
  while (m) {
    const n = Number(m[1]);
    if (n > 0 && out.indexOf(n) < 0) out.push(n);
    m = re.exec(src);
  }
  return out.sort((a, b) => a - b);
}

export function dropEmptyHomeRows(html: string, d: Record<string, unknown>): string {
  try {
    if (!/<!--\s*HOME 1\s*-->/.test(html)) return html;
    if (typeof DOMParser === "undefined") return html;
    const doc = new DOMParser().parseFromString(html, "text/html");
    let touched = false;
    for (const mark of comments(doc.body, [])) {
      const mm = HOME_MARKER.exec(String(mark.data || ""));
      if (!mm) continue;                    /* 1ST COLLECTION / BEGIN Com Table / END Com Table */
      const n = mm[1];
      const hs = String(d["home" + n + "Homesite"] || "").trim();
      const pl = String(d["home" + n + "Plan"] || "").trim();
      if (hs || pl) continue;               /* Homesite OR Plan — same test as rowSummary */
      const row = ownRow(mark);
      if (!row) continue;                   /* can't identify the row — leave the document alone */
      mark.parentNode?.removeChild(mark);
      row.parentNode?.removeChild(row);
      touched = true;
    }
    if (!touched) return html;
    return doc.body.innerHTML;
  } catch { /* a malformed row marker must not take the canvas down */ }
  return html;
}

/* ── AN EMPTY COLLECTION HEADER IS REMOVED, ROW AND ALL (Jack, 2026-08-21:
   "since there's one community it should just be one collection") ──────────────
   The block ships two fixed header rows (`<!-- 1ST COLLECTION -->` /
   `<!-- 2ND COLLECTION -->`). A single-community fill names the first after the
   community and blanks the second — and a header whose name is EMPTY must take
   its row with it, or rows 6–10 sit under a silent whitespace band where the
   heading was. Same marker-labels-the-next-tr contract as dropEmptyHomeRows,
   same DOM (not regex) parsing, same untouched-bytes rule when nothing drops.
   A header still carrying text — including the shipped "Collection Name"
   default on a manual build — is never touched. */
/* ── ANY NUMBER OF COLLECTIONS, NOT JUST TWO (2026-08-22) ─────────────────────
   Layne's block ships `1ST` and `2ND`, and the fill now re-cuts her rows into as
   many sections as the request has communities (services/echExpand) — writing
   `3RD`, `4TH`, `5TH` markers in her own format. A regex pinned to 1ST|2ND left
   those extra headings un-droppable, so an unfilled third section would render as
   a bare bold row. */
/* ── …AND A NAMED HEADER WITH NO HOMES UNDER IT GOES TOO (COS 70141, Justin Bradford,
   2026-10-13) ───────────────────────────────────────────────────────────────────────
   "At the bottom of the table they have a collection that was added with no
   information under it." Five sections re-cut two rows each; the last section's two
   rows were empty, `dropEmptyHomeRows` removed them, and "The Pioneer Collection"
   stayed as a bold heading over nothing. A section runs from its heading marker to the
   next heading marker (or the end of the table); if no home in that span names a
   Homesite or a Plan — the one "is this row real" test both renderings use — the
   heading goes with them. Added collections (`<!--CN-->`, no marker of their own) are
   sections too and follow the same rule. The panel still lists a named section with no
   homes, so a requester can add one. */
const isCollMark = (c: Comment) => COLL_MARKER.test(String(c.data || "")) || String(c.data || "") === "CN";
const homeFilled = (d: Record<string, unknown>, n: string) =>
  !!(String(d["home" + n + "Homesite"] || "").trim() || String(d["home" + n + "Plan"] || "").trim());

const COLL_MARKER = /^\s*(\d+)(?:ST|ND|RD|TH)\s+COLLECTION\s*$/i;
export function dropEmptyCollectionHeaders(html: string, d: Record<string, unknown>): string {
  try {
    if (!/<!--\s*\d+(?:ST|ND|RD|TH) COLLECTION\s*-->|<!--CN-->/i.test(html)) return html;
    if (typeof DOMParser === "undefined") return html;
    const doc = new DOMParser().parseFromString(html, "text/html");
    const all = comments(doc.body, []);
    /* does the section that starts at all[i] hold a home that ships? */
    const sectionHasHome = (i: number): boolean => {
      for (let j = i + 1; j < all.length; j++) {
        const c = all[j];
        if (isCollMark(c)) return false;
        if (/^\s*\/CN\s*$/.test(String(c.data || ""))) continue;
        const hm = HOME_MARKER.exec(String(c.data || ""));
        if (hm && homeFilled(d, hm[1])) return true;
      }
      return false;
    };
    /* a table with no home markers at all is not ours to judge */
    const hasHomeMarkers = all.some((c) => HOME_MARKER.test(String(c.data || "")));
    let touched = false;
    all.forEach((mark, i) => {
      if (!mark.parentNode) return;
      const isCN = String(mark.data || "") === "CN";
      const mm = isCN ? null : COLL_MARKER.exec(String(mark.data || ""));
      if (!isCN && !mm) return;
      const named = isCN || !!String(d["collection" + (Number(mm![1]) || 1) + "Name"] || "").trim();
      const empty = hasHomeMarkers && !sectionHasHome(i);
      if (named && !empty) return;
      if (isCN) {
        /* an added collection: its row is the nearest <tr> around the literal */
        const row = (mark.parentNode as Element | null)?.closest?.("tr") || null;
        if (!row) return;
        row.parentNode?.removeChild(row);
        touched = true;
        return;
      }
      const row = ownRow(mark);
      if (!row) return;
      mark.parentNode?.removeChild(mark);
      row.parentNode?.removeChild(row);
      touched = true;
    });
    if (!touched) return html;
    return doc.body.innerHTML;
  } catch { /* a malformed marker must not take the canvas down */ }
  return html;
}

/* ── WHICH COLLECTION OWNS WHICH HOME — READ OFF THE BLOCK, NEVER ASSUMED ──────
   Jack, after finding the panel he was editing was our legacy block rather than
   Layne's: "make sure Layne's is in the right panel reflecting what we've updated."

   Her ECH_ListingTable declares TWO collection names and ten homes, and the
   ownership is stated by the ORDER OF THE TOKENS IN HER MARKUP — measured on the
   live block 2026-08-20:

     collection1Name → home1..home5
     collection2Name → home6..home10

   That 5/5 split is a fact about her current markup and nothing more. Hard-coding
   it here is exactly the mistake this file's siblings spent the day undoing, so it
   is PARSED from `d.__html` instead: if she re-cuts the block as 3 collections, or
   as 4 + 6, the panel follows with no code change. A block with no collection token
   yields a single unnamed group, which is the honest answer for one.                */
export interface RowCollection {
  /** the field key holding the name, e.g. `collection1Name` — null when unnamed
      AND for an ADDED collection (see `lit`) */
  key: string | null;
  /** the home numbers this collection owns, in markup order */
  homes: number[];
  /** an ADDED collection's literal name (2026-08-21) — the render substitutes
      DECLARED tokens only, so a duplicated collection row carries its name as
      sentinel-wrapped text, not a token. `undefined` on token collections. */
  lit?: string;
}

/* ── EXTRA COLLECTIONS (2026-08-21, Jack: "missing the opportunity to add more
   collections than just two") — sanctioned by the block's OWN guardrail note:
   "Additional collections beyond the two provided may be added by duplicating a
   collection row, as long as total home rows stay at 10 or fewer." The clone is
   the block's own collection <tr> with the token replaced by sentinel-wrapped
   LITERAL text (renameable), and the insertions are recorded on the block
   (`__extraColls`) so a markup refresh can re-apply them to fresher html. */
export const EXTRA_COLLS = "__extraColls";
export interface ExtraColl { slot: number; label: string }

const COLL_ROW_RE = /<tr[^>]*>(?:(?!<\/tr>)[\s\S])*\{\{\s*collection1Name\s*\}\}(?:(?!<\/tr>)[\s\S])*<\/tr>/;
const CN_RE = /<!--CN-->([\s\S]*?)<!--\/CN-->/g;
const litEsc = (s: string) => String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/** duplicate the block's own collection row before `<!-- HOME slot -->` */
export function rowAddColl(html: unknown, slot: number, label: string): string | null {
  const h = String(html ?? "");
  const tpl = COLL_ROW_RE.exec(h);
  if (!tpl) return null;
  const at = h.search(new RegExp("<!--\\s*HOME\\s+" + slot + "\\s*-->"));
  if (at < 0) return null;
  const row = tpl[0].replace(/\{\{\s*collection1Name\s*\}\}/, "<!--CN-->" + litEsc(label) + "<!--/CN-->") + "\n";
  return h.slice(0, at) + row + h.slice(at);
}

/** rename the `ord`-th ADDED collection (markup order) */
export function rowRenameColl(html: unknown, ord: number, label: string): string {
  let i = -1;
  return String(html ?? "").replace(CN_RE, (m) => {
    i++;
    return i === ord ? "<!--CN-->" + litEsc(label) + "<!--/CN-->" : m;
  });
}

/**
 * ── REMOVE AN ADDED COLLECTION (2026-08-22, Jack: "should have the option to remove a
 * collection") ─────────────────────────────────────────────────────────────────────
 * Drops the `ord`-th ADDED collection row from the markup. Only added rows can go: the
 * block's own two collections are Layne's structure — the token row carries
 * `{{collection1Name}}`, an added one carries the `<!--CN-->` literal — and an added row
 * is the only one this repo put there in the first place.
 * The row is matched from the sentinel OUTWARDS to its enclosing `<tr>`, so it cannot
 * take a neighbour with it, and the homes below keep their slots: a collection row is a
 * heading, not a container, so removing it merges its homes into the section above,
 * which is exactly what the requester sees on the canvas.
 */
export function rowDropColl(html: unknown, ord: number): string | null {
  const h = String(html ?? "");
  let i = -1;
  let hit = -1;
  const re = /<!--CN-->[\s\S]*?<!--\/CN-->/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(h))) { i++; if (i === ord) { hit = m.index; break; } }
  if (hit < 0) return null;
  const open = h.lastIndexOf("<tr", hit);
  if (open < 0) return null;
  const closeAt = h.indexOf("</tr>", hit);
  if (closeAt < 0) return null;
  const end = closeAt + "</tr>".length;
  /* the newline `rowAddColl` appended, if it is still there */
  const tail = h.slice(end, end + 1) === "\n" ? end + 1 : end;
  return h.slice(0, open) + h.slice(tail);
}

/** re-apply recorded insertions to FRESH markup — the rebuild's transplant */
export function rowApplyExtraColls(html: unknown, extra: unknown): string {
  let h = String(html ?? "");
  const list = Array.isArray(extra) ? (extra as ExtraColl[]).filter((e) => e && e.slot) : [];
  list.slice().sort((a, b) => a.slot - b.slot).forEach((e) => {
    const next = rowAddColl(h, e.slot, String(e.label ?? ""));
    if (next) h = next;
  });
  return h;
}

export function rowCollections(html: unknown, rows: readonly number[]): RowCollection[] {
  const known = new Set(rows);
  const out: RowCollection[] = [];
  try {
    const h = String(html ?? "");
    const re = /\{\{\s*(collection(\d+)Name|home(\d+)[A-Za-z]+)\s*\}\}|<!--CN-->([\s\S]*?)<!--\/CN-->/g;
    let m: RegExpExecArray | null;
    let cur: RowCollection | null = null;
    const seen = new Set<number>();
    while ((m = re.exec(h))) {
      if (m[4] !== undefined) { cur = { key: null, homes: [], lit: m[4] }; out.push(cur); continue; }
      if (m[2]) { cur = { key: m[1], homes: [] }; out.push(cur); continue; }
      const n = +(m[3] || 0);
      if (!n || seen.has(n) || !known.has(n)) continue;   /* one entry per home, declared rows only */
      seen.add(n);
      if (!cur) { cur = { key: null, homes: [] }; out.push(cur); }
      cur.homes.push(n);
    }
    /* any declared row the markup never mentions still has to be editable */
    const orphans = rows.filter((n) => !seen.has(n));
    if (orphans.length) {
      if (!out.length) out.push({ key: null, homes: [] });
      out[out.length - 1].homes.push(...orphans);
    }
  } catch { /* a malformed block must not take the panel down */ }
  /* no tokens at all → one unnamed group holding everything */
  if (!out.length) return [{ key: null, homes: rows.slice() }];
  return out.filter((g) => g.key || g.lit != null || g.homes.length);
}

/* ══════════════════════════════════════════════════════════════════════════════
   TWO MORE INSTRUCTIONS THE BLOCKS GAVE US AND NOBODY IMPLEMENTED. 2026-08-23.

   Found by sweeping every block's @GUARDRAILS *notes* after Jack asked why the
   third collection was hard: the notes are a contract, and three of them describe
   markup surgery the app has to perform. One was the listing table's extra
   collections (done — services/echExpand). These are the other two, both verified
   against the live block html:

     ECH_SocialIcons  "Icons may be removed if a channel is not needed. Remove the
                       icon's entire socialshare-innertable block AND its adjacent
                       Outlook (MSO) conditional cell divider. Removing the image
                       alone leaves a gap in Outlook."
                      → Toggling a channel off in the panel only cleared its URL, so
                        the email still shipped the icon, linked to nothing.

     ECH_GOInvite1CTA "The masterplan community line is optional. If it does not
     ECH_GOInvite2CTA  apply, delete the entire div between the masterplan markers.
                       Do not leave it blank, or the block renders with a dangling
                       gap."
                      → No code referenced those markers at all, so a community-level
                        Grand Opening rendered an empty 28px line under the name.
   ══════════════════════════════════════════════════════════════════════════════ */

/** `<!-- ICON: Facebook … -->` → the field whose value decides whether it ships */
const ICON_FIELD: Record<string, string> = {
  facebook: "facebookUrl", x: "xUrl", instagram: "instagramUrl",
  pinterest: "pinterestUrl", youtube: "youtubeUrl",
};

/**
 * An icon with no URL takes its table AND the MSO divider that follows it — the
 * removal the block's own note spells out, done in string space because the MSO
 * divider is an HTML COMMENT (`<!--[if mso]></td><td …><![endif]-->`) and a DOM
 * pass would have to reason about comment siblings to catch it.
 */
export function dropEmptyIconBlocks(html: string, d: Record<string, unknown>): string {
  let out = String(html || "");
  if (!/<!--\s*ICON:/i.test(out)) return out;
  const re = /<!--\s*ICON:\s*([A-Za-z]+)[^>]*-->\s*<table[^>]*socialshare-innertable[\s\S]*?<\/table>\s*(?:<!--\[if mso\]>[\s\S]*?<!\[endif\]-->)?/gi;
  out = out.replace(re, (whole, chan: string) => {
    const key = ICON_FIELD[String(chan || "").toLowerCase()];
    if (!key) return whole;                       /* a channel we don't know: leave it */
    return String(d[key] || "").trim() ? whole : "";
  });
  return out;
}

/**
 * An OPTIONAL region — `<!-- X LINE START … --> … <!-- X LINE END -->` — goes when
 * every token inside it is empty. Layne writes the instruction into the marker
 * itself ("delete this div entirely if the masterplan does not apply"), so the
 * marker pair IS the contract and no block name is hard-coded here.
 */
export function dropEmptyOptionalLines(html: string, d: Record<string, unknown>): string {
  let out = String(html || "");
  if (!/LINE START/i.test(out)) return out;
  const re = /<!--\s*([A-Z][A-Z ]*?)\s+LINE START[^>]*-->([\s\S]*?)<!--\s*\1\s+LINE END\s*-->/gi;
  out = out.replace(re, (whole, _name: string, inner: string) => {
    const toks = String(inner).match(/\{\{([A-Za-z0-9_]+)\}\}/g) || [];
    if (!toks.length) return whole;               /* static copy, not a fed line */
    const anyFilled = toks.some((t) => String(d[t.slice(2, -2)] || "").trim());
    return anyFilled ? whole : "";
  });
  return out;
}

/* ── NO DANGLING SLASHES IN A LISTING ROW (2026-09-18, Laura Hughes / CHB, Form Feedback
   rec4wszVpAEn6uCFt 09-14: "Removing extra slashes in the table") ──────────────────────
   Layne's row cells are literal `{{homeNHomesite}} / {{homeNPlan}}` and
   `{{homeNSqFt}} / {{homeNBeds}} / {{homeNBaths}}`. A plan-only row (no homesite number),
   or a plan the feed lists without a bed count, rendered "/ Sienna II" and "2,663 / / 3.5"
   (measured on the real block). The separators belong to the values, not the cell: after
   substitution, a cell whose text is only slash-separated values keeps the values it has and
   the slashes between them — nothing leading, nothing trailing, nothing doubled. Cells with
   markup inside (the View link) are untouched. */
export function tidyRowSlashes(html: string): string {
  if (!/<td[^>]*>\s*[^<]*\/[^<]*<\/td>/.test(html)) return html;
  return html.replace(/(<td[^>]*>)(\s*)([^<]*\/[^<]*?)(\s*)(<\/td>)/g, (whole, open: string, lead: string, text: string, tail: string, close: string) => {
    if (!/\//.test(text)) return whole;
    const parts = text.split("/").map((p) => p.trim()).filter(Boolean);
    return open + lead + parts.join(" / ") + tail + close;
  });
}
